import React, { useMemo } from 'react';
import { Button } from 'rsuite';
import ProfilePhoto from '../employees/ProfilePhoto';
import { getCategoryLabel } from '../../utils/expenseCategories';
import { describeExpenseGroup } from '../../utils/expenseGroups';
import {
  employeeName,
  formatAge,
  formatCurrency,
  formatShortDate,
} from './approvalFormat';
import './ApprovalCards.css';

// Pending first, then rejected, then approved. What is still to decide belongs
// at the top; a rejection above the approvals because it is the one decided row
// that may still come back.
const STATUS_ORDER = { pending: 0, rejected: 1, approved: 2 };

const statusKey = (expense) => String(expense?.status || '').toLowerCase();
const statusRank = (expense) => STATUS_ORDER[statusKey(expense)] ?? 3;

const STATUS_LABELS = {
  draft: 'Not submitted',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/**
 * A submitted batch of expenses, listed as compact rows inside one card.
 *
 * A batch of eleven receipts costs one card in the queue instead of eleven, and
 * the rows carry only what tells them apart — when, what kind, how much — so the
 * whole batch is legible at a glance. Approval is still per expense: a row opens
 * the same review modal a standalone card does — a pending one to decide it, an
 * already-decided one to read what was decided and why.
 *
 * `onOpen` adds a button for whatever is done to the batch as a whole — adding to
 * it, submitting it — which is the employee's side of the same card.
 */
export default function ApprovalGroupCard({
  group,
  onReview,
  onOpen,
  openLabel,
  clearingIds = [],
}) {
  const rows = useMemo(() => {
    const expenses = [...(group?.expenses || [])];
    expenses.sort((a, b) => (
      statusRank(a) - statusRank(b)
      // Oldest first inside a status, matching the order of the queue itself.
      || String(a.purchase_date || '').localeCompare(String(b.purchase_date || ''))
    ));
    return expenses;
  }, [group?.expenses]);

  if (!group) return null;

  const canOpenRow = typeof onReview === 'function';
  const submitter = employeeName(group.expenses?.[0]);
  const age = formatAge(group.submittedAt || group.expenses?.[0]?.created_at);
  // One line covering every state a batch can be in — draft, part-decided,
  // finished — so the card never has to special-case its own progress.
  const progress = describeExpenseGroup(group);

  return (
    <article className="approval-group" aria-label={`Expense group ${group.name}`}>
      <header className="approval-group__head">
        <ProfilePhoto
          employeeId={group.employeeId}
          size="small"
          firstName={group.expenses?.[0]?.first_name}
          lastName={group.expenses?.[0]?.last_name}
        />
        <div className="approval-group__who">
          <div className="approval-group__name" title={group.name}>
            <i className="bi bi-collection" aria-hidden="true" /> {group.name}
          </div>
          <div className="approval-group__age">
            {submitter}
            {age ? ` · submitted ${age}` : ''}
          </div>
        </div>
        <span className="approval-group__total">{formatCurrency(group.total)}</span>
      </header>

      <div className="approval-group__meta">
        {group.count} expense{group.count === 1 ? '' : 's'}
        {progress ? ` · ${progress}` : ''}
      </div>

      {/* Scrolls inside the card once the batch outgrows it, so one large group
          can't stretch the row every other card in it is measured against. */}
      <ul className="approval-group__list">
        {rows.map((expense) => {
          const key = statusKey(expense);
          const clearing = clearingIds.includes(String(expense.id));

          return (
            <li key={expense.id}>
              <div
                className={`approval-group__row approval-group__row--${key}${clearing ? ' approval-group__row--clearing' : ''}`}
                // Only a control where there is something to open; a list with
                // no handler shouldn't advertise a click that does nothing.
                role={canOpenRow ? 'button' : undefined}
                tabIndex={canOpenRow ? 0 : undefined}
                onClick={canOpenRow ? () => onReview(expense) : undefined}
                onKeyDown={canOpenRow ? (event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onReview(expense);
                } : undefined}
              >
                <span className="approval-group__row-date">
                  {formatShortDate(expense.purchase_date) || '—'}
                </span>
                <span className="approval-group__row-category" title={getCategoryLabel(expense.category)}>
                  {getCategoryLabel(expense.category) || '—'}
                </span>
                <span className="approval-group__row-amount">{formatCurrency(expense.amount)}</span>
                <span className={`approval-group__row-status approval-group__row-status--${key}`}>
                  {STATUS_LABELS[key] || expense.status || '—'}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {onOpen ? (
        <div className="approval-group__foot">
          <Button appearance="ghost" block onClick={() => onOpen(group)}>
            {openLabel || (group.isSubmitted ? 'Open group' : 'Open to add or submit')}
          </Button>
        </div>
      ) : null}
    </article>
  );
}
